import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { DialogCloseReason } from './types';

export interface ImmersiveModalWrapperProps {
  /** The content to display in the modal */
  children: ReactNode;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback fired when the modal is closed */
  onClose?: (e: unknown, reason: DialogCloseReason) => void;
}

/**
 * ImmersiveModalWrapper component that displays a fullscreen modal.
 * This wrapper provides an immersive experience by taking over the entire viewport.
 *
 * @example
 * ```tsx
 * <ImmersiveModalWrapper isOpen={isOpen} onClose={onClose}>
 *   <div className="min-h-0 flex-1 overflow-hidden">
 *     <YourContent />
 *   </div>
 * </ImmersiveModalWrapper>
 * ```
 */
export const ImmersiveModalWrapper: React.FC<ImmersiveModalWrapperProps> = ({
  children,
  isOpen,
  onClose,
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && onClose) {
        onClose(e, 'escapeKeyDown');
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && onClose) {
      onClose(e, 'backdropClick');
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-modal flex items-center justify-center`}
      onClick={handleBackdropClick}
      role="presentation"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal Content */}
      <div
        className="relative z-[9999] h-full w-full bg-white"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex h-full w-full flex-col overflow-hidden">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};
